import styles from '@github-ui/commenting/AvatarStyles.module.css'
import {IssueComment} from '@github-ui/commenting/IssueComment'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {AddedToProjectV2Event} from '@github-ui/timeline-items/AddedToProjectV2Event'
import {AssignedEvent} from '@github-ui/timeline-items/AssignedEvent'
import {BlockedByAddedEvent} from '@github-ui/timeline-items/BlockedByAddedEvent'
import {BlockedByRemovedEvent} from '@github-ui/timeline-items/BlockedByRemovedEvent'
import {BlockingAddedEvent} from '@github-ui/timeline-items/BlockingAddedEvent'
import {BlockingRemovedEvent} from '@github-ui/timeline-items/BlockingRemovedEvent'
import {ClosedEvent} from '@github-ui/timeline-items/ClosedEvent'
import {CommentDeletedEvent} from '@github-ui/timeline-items/CommentDeletedEvent'
import {ConnectedEvent} from '@github-ui/timeline-items/ConnectedEvent'
import {ConvertedFromDraftEvent} from '@github-ui/timeline-items/ConvertedFromDraftEvent'
import {ConvertedToDiscussionEvent} from '@github-ui/timeline-items/ConvertedToDiscussionEvent'
import {CrossReferencedEvent, type ReferenceTypes} from '@github-ui/timeline-items/CrossReferencedEvent'
import {DemilestonedEvent} from '@github-ui/timeline-items/DemilestonedEvent'
import {DisconnectedEvent} from '@github-ui/timeline-items/DisconnectedEvent'
import {IssueCommentPinnedEvent} from '@github-ui/timeline-items/IssueCommentPinnedEvent'
import {IssueCommentUnpinnedEvent} from '@github-ui/timeline-items/IssueCommentUnpinnedEvent'
import {IssueFieldAddedEvent} from '@github-ui/timeline-items/IssueFieldAddedEvent'
import {IssueFieldChangedEvent} from '@github-ui/timeline-items/IssueFieldChangedEvent'
import {IssueFieldRemovedEvent} from '@github-ui/timeline-items/IssueFieldRemovedEvent'
import {IssueTypeAddedEvent} from '@github-ui/timeline-items/IssueTypeAddedEvent'
import {IssueTypeChangedEvent} from '@github-ui/timeline-items/IssueTypeChangedEvent'
import {IssueTypeRemovedEvent} from '@github-ui/timeline-items/IssueTypeRemovedEvent'
import {LabeledEvent} from '@github-ui/timeline-items/LabeledEvent'
import {wrapElement} from '@github-ui/timeline-items/LayoutHelpers'
import {LockedEvent} from '@github-ui/timeline-items/LockedEvent'
import {MarkedAsDuplicateEvent} from '@github-ui/timeline-items/MarkedAsDuplicateEvent'
import {MentionedEvent} from '@github-ui/timeline-items/MentionedEvent'
import {MilestonedEvent} from '@github-ui/timeline-items/MilestonedEvent'
import {ParentIssueAddedEvent} from '@github-ui/timeline-items/ParentIssueAddedEvent'
import {ParentIssueRemovedEvent} from '@github-ui/timeline-items/ParentIssueRemovedEvent'
import {PinnedEvent} from '@github-ui/timeline-items/PinnedEvent'
import {ProjectV2ItemStatusChangedEvent} from '@github-ui/timeline-items/ProjectV2ItemStatusChangedEvent'
import {ReferencedEvent} from '@github-ui/timeline-items/ReferencedEvent'
import {RelatesToAddedEvent} from '@github-ui/timeline-items/RelatesToAddedEvent'
import {RelatesToRemovedEvent} from '@github-ui/timeline-items/RelatesToRemovedEvent'
import {RemovedFromProjectV2Event} from '@github-ui/timeline-items/RemovedFromProjectV2Event'
import {RenamedTitleEvent} from '@github-ui/timeline-items/RenamedTitleEvent'
import {ReopenedEvent} from '@github-ui/timeline-items/ReopenedEvent'
import type {SecondaryTimelineSource} from '@github-ui/timeline-items/SecondaryTimelineSource'
import {SubIssueAddedEvent} from '@github-ui/timeline-items/SubIssueAddedEvent'
import {SubIssueRemovedEvent} from '@github-ui/timeline-items/SubIssueRemovedEvent'
import {SubscribedEvent} from '@github-ui/timeline-items/SubscribedEvent'
import {timelineItemKey} from '@github-ui/timeline-items/TimelineItemKey'
import {TimelineRowBorder, type TimelineRowBorderCommentParams} from '@github-ui/timeline-items/TimelineRowBorder'
import {TimelineRowPassthroughContext} from '@github-ui/timeline-items/TimelineRowPassthroughContext'
import {TransferredEvent} from '@github-ui/timeline-items/TransferredEvent'
import {UnassignedEvent} from '@github-ui/timeline-items/UnassignedEvent'
import {UnlabeledEvent} from '@github-ui/timeline-items/UnlabeledEvent'
import {UnlockedEvent} from '@github-ui/timeline-items/UnlockedEvent'
import {UnmarkedAsDuplicateEvent} from '@github-ui/timeline-items/UnmarkedAsDuplicateEvent'
import {UnpinnedEvent} from '@github-ui/timeline-items/UnpinnedEvent'
import {UnsubscribedEvent} from '@github-ui/timeline-items/UnsubscribedEvent'
import {UserBlockedEvent} from '@github-ui/timeline-items/UserBlockedEvent'
import {VALUES} from '@github-ui/timeline-items/Values'
import {Link} from '@primer/react'
import {type ReactElement, type RefObject, useMemo} from 'react'

import type {RolledUpTimelineItem} from '../../utils/timeline-rollups'
import type {IssueTimelineItemNode} from '../issue-viewer-page-query-types'
import type {OptionConfig} from '../OptionConfig'
import {computeEventRowPassthrough} from './compute-event-row-passthrough'

export type IssueTimelineItemFragment = IssueTimelineItemNode

type TimelineItemTypename = IssueTimelineItemFragment['__typename']

/**
 * Required props to render a timeline item event component.
 */
type EventItemInnerProps<TTypename extends TimelineItemTypename = TimelineItemTypename> = {
  queryRef: Extract<IssueTimelineItemFragment, {readonly __typename: TTypename}>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rollupGroup?: Record<string, any[]>
  currentIssueId: string
  repositoryId: string
  repositoryNameWithOwner: string
  ownerLogin: string
  issueUrl: string
  timelineEventBaseUrl: string
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLLIElement | null>
  referenceTypes?: ReferenceTypes
  viewer: string | null
  onLinkClick?: (event: MouseEvent) => void
  secondaryTimelineSource?: SecondaryTimelineSource
}

/**
 * Record of all possible timeline items that can be rendered (IssueComment is not included).
 *
 * Keying on `__typename` narrows `queryRef` to the matching union member, so each renderer receives
 * exactly the shape its component declares.
 */
const TIMELINE_ITEMS: {
  [TTypename in TimelineItemTypename]?: (args: EventItemInnerProps<TTypename>) => React.ReactElement | null
} = {
  SubscribedEvent: ({queryRef, ...props}) => <SubscribedEvent {...props} queryRef={queryRef} />,
  UnsubscribedEvent: ({queryRef, ...props}) => <UnsubscribedEvent {...props} queryRef={queryRef} />,
  MentionedEvent: ({queryRef, ...props}) => <MentionedEvent {...props} queryRef={queryRef} />,
  ClosedEvent: ({queryRef, ...props}) => <ClosedEvent {...props} queryRef={queryRef} />,
  ReopenedEvent: ({queryRef, ...props}) => <ReopenedEvent {...props} queryRef={queryRef} />,
  RenamedTitleEvent: ({queryRef, ...props}) => <RenamedTitleEvent {...props} queryRef={queryRef} />,
  LockedEvent: ({queryRef, ...props}) => <LockedEvent {...props} queryRef={queryRef} />,
  UnlockedEvent: ({queryRef, ...props}) => <UnlockedEvent {...props} queryRef={queryRef} />,
  PinnedEvent: ({queryRef, ...props}) => <PinnedEvent {...props} queryRef={queryRef} />,
  IssueCommentPinnedEvent: ({queryRef, ...props}) => <IssueCommentPinnedEvent {...props} queryRef={queryRef} />,
  UnpinnedEvent: ({queryRef, ...props}) => <UnpinnedEvent {...props} queryRef={queryRef} />,
  IssueCommentUnpinnedEvent: ({queryRef, ...props}) => <IssueCommentUnpinnedEvent {...props} queryRef={queryRef} />,
  LabeledEvent: ({queryRef, ...props}) => <LabeledEvent {...props} queryRef={queryRef} />,
  UnlabeledEvent: ({queryRef, ...props}) => <UnlabeledEvent {...props} queryRef={queryRef} />,
  UnassignedEvent: ({queryRef, ...props}) => <UnassignedEvent {...props} queryRef={queryRef} />,
  AssignedEvent: ({queryRef, ...props}) => <AssignedEvent {...props} queryRef={queryRef} />,
  BlockedByAddedEvent: ({queryRef, ...props}) => <BlockedByAddedEvent {...props} queryRef={queryRef} />,
  BlockedByRemovedEvent: ({queryRef, ...props}) => <BlockedByRemovedEvent {...props} queryRef={queryRef} />,
  BlockingAddedEvent: ({queryRef, ...props}) => <BlockingAddedEvent {...props} queryRef={queryRef} />,
  BlockingRemovedEvent: ({queryRef, ...props}) => <BlockingRemovedEvent {...props} queryRef={queryRef} />,
  RelatesToAddedEvent: ({queryRef, ...props}) => <RelatesToAddedEvent {...props} queryRef={queryRef} />,
  RelatesToRemovedEvent: ({queryRef, ...props}) => <RelatesToRemovedEvent {...props} queryRef={queryRef} />,
  CommentDeletedEvent: ({queryRef, ...props}) => <CommentDeletedEvent {...props} queryRef={queryRef} />,
  UserBlockedEvent: ({queryRef, ...props}) => <UserBlockedEvent {...props} queryRef={queryRef} />,
  CrossReferencedEvent: ({queryRef, ...props}) => <CrossReferencedEvent {...props} queryRef={queryRef} />,
  MilestonedEvent: ({queryRef, ...props}) => <MilestonedEvent {...props} queryRef={queryRef} />,
  DemilestonedEvent: ({queryRef, ...props}) => <DemilestonedEvent {...props} queryRef={queryRef} />,
  ReferencedEvent: ({queryRef, ...props}) => <ReferencedEvent {...props} queryRef={queryRef} />,
  ConnectedEvent: ({queryRef, ...props}) => <ConnectedEvent {...props} queryRef={queryRef} />,
  TransferredEvent: ({queryRef, ...props}) => <TransferredEvent {...props} queryRef={queryRef} />,
  DisconnectedEvent: ({queryRef, ...props}) => <DisconnectedEvent {...props} queryRef={queryRef} />,
  MarkedAsDuplicateEvent: ({queryRef, ...props}) => <MarkedAsDuplicateEvent {...props} queryRef={queryRef} />,
  UnmarkedAsDuplicateEvent: ({queryRef, ...props}) => <UnmarkedAsDuplicateEvent {...props} queryRef={queryRef} />,
  ConvertedToDiscussionEvent: ({queryRef, ...props}) => <ConvertedToDiscussionEvent {...props} queryRef={queryRef} />,
  AddedToProjectV2Event: ({queryRef, ...props}) => <AddedToProjectV2Event {...props} queryRef={queryRef} />,
  RemovedFromProjectV2Event: ({queryRef, ...props}) => <RemovedFromProjectV2Event {...props} queryRef={queryRef} />,
  ProjectV2ItemStatusChangedEvent: ({queryRef, ...props}) => (
    <ProjectV2ItemStatusChangedEvent {...props} queryRef={queryRef} />
  ),
  ConvertedFromDraftEvent: ({queryRef, ...props}) => <ConvertedFromDraftEvent {...props} queryRef={queryRef} />,
  SubIssueAddedEvent: ({queryRef, ...props}) => <SubIssueAddedEvent {...props} queryRef={queryRef} />,
  SubIssueRemovedEvent: ({queryRef, ...props}) => <SubIssueRemovedEvent {...props} queryRef={queryRef} />,
  ParentIssueAddedEvent: ({queryRef, ...props}) => <ParentIssueAddedEvent {...props} queryRef={queryRef} />,
  ParentIssueRemovedEvent: ({queryRef, ...props}) => <ParentIssueRemovedEvent {...props} queryRef={queryRef} />,
  IssueTypeAddedEvent: ({queryRef, ...props}) => <IssueTypeAddedEvent {...props} queryRef={queryRef} />,
  IssueTypeRemovedEvent: ({queryRef, ...props}) => <IssueTypeRemovedEvent {...props} queryRef={queryRef} />,
  IssueTypeChangedEvent: ({queryRef, ...props}) => <IssueTypeChangedEvent {...props} queryRef={queryRef} />,
  IssueFieldAddedEvent: ({queryRef, ...props}) => <IssueFieldAddedEvent {...props} queryRef={queryRef} />,
  IssueFieldChangedEvent: ({queryRef, ...props}) => <IssueFieldChangedEvent {...props} queryRef={queryRef} />,
  IssueFieldRemovedEvent: ({queryRef, ...props}) => <IssueFieldRemovedEvent {...props} queryRef={queryRef} />,
}

/**
 * Props exclusively propagated to render IssueComment and the other timeline event components.
 *
 * Not used by IssueTimelineItem itself.
 */
export type EventProps = {
  viewer: string | null
  onCommentChange: (id: string) => void
  onCommentReply: (quotedComment: string) => void
  onCommentEditCancel: (id: string) => void
  optionConfig: OptionConfig
  issueUpdatesChannel?: string | null
}

export type IssueTimelineItemProps = {
  item: RolledUpTimelineItem<IssueTimelineItemFragment>
  issueId: string
  repositoryId: string
  repositoryNameWithOwner: string
  ownerLogin: string
  issueUrl: string
  isHighlighted?: boolean
  addDivider?: boolean
  showDividerLine?: boolean
  refAttribute?: RefObject<HTMLLIElement | null>
  secondaryTimelineSource?: SecondaryTimelineSource
} & EventProps

/**
 * This component renders a single timeline item which can be a timeline event or a comment.
 *
 * It takes a rollup group item as it is intended to support rollup groups.
 */
export const IssueTimelineItem = ({
  item: {item, rollupGroup},
  issueId,
  repositoryId,
  repositoryNameWithOwner,
  ownerLogin,
  issueUrl,
  isHighlighted,
  viewer,
  onCommentChange,
  onCommentReply,
  onCommentEditCancel,
  addDivider = false,
  showDividerLine = true,
  optionConfig,
  refAttribute,
  secondaryTimelineSource,
  issueUpdatesChannel,
}: IssueTimelineItemProps) => {
  // Memoized so the passthrough context value stays referentially stable across renders
  // (a fresh object each render forces every consuming Timeline.Item to re-render). Only
  // the flag-on event branch reads it; it is undefined when the item is absent.
  const eventRowPassthrough = useMemo(
    () => (item ? computeEventRowPassthrough({itemId: timelineItemKey(item), addDivider}) : undefined),
    [item, addDivider],
  )

  if (!item) return null

  const useListSemantics = isFeatureEnabled('primer_react_timeline_list_semantics')

  const isMajorEvent = VALUES.timeline.majorEventTypes.includes(item.__typename)

  // Flag-off (production) path: Primer's Timeline.Item and the TimelineRowBorder inner
  // container still render <div>s, so the HTMLLIElement-typed highlight ref is attached
  // here with a localized cast, exactly as it was before this change. This keeps the
  // production scroll-to-highlighted target (the TimelineRowBorder container, resolved via
  // React's post-order ref commit) byte-for-byte identical. Under the flag these elements
  // become real <li>s and the ref lands on a single <li> below, so no cast is needed there.
  const flagOffHighlightRef = useListSemantics
    ? undefined
    : (refAttribute as RefObject<HTMLDivElement | null> | undefined)

  let itemToRender: ReactElement | null = null
  let commentParams: TimelineRowBorderCommentParams | undefined = undefined
  if (item.__typename === 'IssueComment') {
    commentParams = {
      first: false,
      last: false,
      viewerDidAuthor: item.viewerDidAuthor,
    }
    itemToRender = (
      <IssueComment
        comment={item}
        commentSubjectAuthorLogin={item.issue?.author?.login ?? ''}
        onChange={() => onCommentChange?.(timelineItemKey(item))}
        onEditCancel={() => onCommentEditCancel?.(timelineItemKey(item))}
        onReply={quote => onCommentReply?.(quote)}
        onSave={() => onCommentEditCancel?.(timelineItemKey(item))}
        highlightedCommentId={isHighlighted ? String(item.databaseId) : undefined}
        navigate={optionConfig.navigate}
        refAttribute={flagOffHighlightRef}
        onLinkClick={optionConfig.onLinkClick}
        optimisticConnection={{ownerId: issueId, connectionPath: ['timelineItems']}}
        issueUpdatesChannel={issueUpdatesChannel}
      />
    )
  }

  // The renderers are keyed by `__typename`, but TypeScript cannot correlate the lookup with the
  // narrowed `item`, so it intersects every renderer's props down to `never`. The runtime key is
  // the item's own `__typename`, so the widened signature is the one that actually applies.
  const TimelineReactElement = TIMELINE_ITEMS[item.__typename] as
    | ((args: EventItemInnerProps) => React.ReactElement | null)
    | undefined
  if (TimelineReactElement) {
    itemToRender = (
      <TimelineReactElement
        key={timelineItemKey(item)}
        queryRef={item}
        rollupGroup={rollupGroup}
        currentIssueId={issueId}
        issueUrl={issueUrl}
        repositoryId={repositoryId}
        repositoryNameWithOwner={repositoryNameWithOwner}
        ownerLogin={ownerLogin}
        highlightedEventId={isHighlighted ? String(item.databaseId) : undefined}
        timelineEventBaseUrl={optionConfig.timelineEventBaseUrl || '/issues'}
        refAttribute={refAttribute}
        onLinkClick={optionConfig.onLinkClick}
        viewer={viewer}
        secondaryTimelineSource={secondaryTimelineSource}
      />
    )
  }

  const wrappedElement = (
    <TimelineRowBorder
      key={timelineItemKey(item)}
      ref={flagOffHighlightRef}
      item={item}
      addDivider={addDivider}
      showDividerLine={showDividerLine}
      isMajor={isMajorEvent && VALUES.timeline.borderedMajorEventTypes.includes(item.__typename)}
      isHighlighted={item.__typename === 'IssueComment' && isHighlighted}
      commentParams={commentParams}
    >
      {itemToRender}
    </TimelineRowBorder>
  )

  const leadingElement =
    item.__typename === 'IssueComment' && !isFeatureEnabled('issue_inline_avatars') ? (
      <TimelineItemAvatar item={item} addDivider={addDivider} />
    ) : undefined

  // Under primer_react_timeline_list_semantics, <Timeline> renders as an <ol> and
  // each <Timeline.Item> as an <li>. An <ol> is only valid markup when its direct
  // children are <li>, so we must not place the extra wrapper <div>s (wrapElement /
  // TimelineRowBorder) between the <ol> and the <li>.
  //  - Events already render their own <li> (Timeline.Item, created deep in
  //    timeline-items), so we return the event element directly and let the highlight
  //    ref land on that single <li> via TimelineRow (cast-free, HTMLLIElement).
  //  - Comments have no Timeline.Item, so issue-viewer owns a plain <li> here and holds
  //    the highlight ref on it (also HTMLLIElement, cast-free); the existing row
  //    treatment is reproduced inside that <li> via wrapElement.
  if (useListSemantics) {
    if (item.__typename === 'IssueComment') {
      // data-wrapper-timeline-id must live on this <li>, not on the inner wrapElement
      // div. resolveTimelineFocusTarget finds the anchor by that attribute and then
      // walks its nextElementSibling to reach the newly-loaded rows after "Load more".
      // Newly-loaded rows are <li> siblings of this <li>, so the anchor has to sit at
      // the <li> level. If it stayed on the inner div (the sole child of the <li>),
      // nextElementSibling would be null and focus would never advance. We therefore
      // omit the id arg to wrapElement so the div no longer duplicates the attribute.
      return (
        <li ref={refAttribute} data-wrapper-timeline-id={timelineItemKey(item)}>
          {wrapElement(wrappedElement, leadingElement)}
        </li>
      )
    }
    // Events render their own Timeline.Item <li> deep in timeline-items, so
    // issue-viewer cannot wrap or style it directly. It supplies the row-box
    // padding, the data attributes, and the group-boundary spacing that
    // TimelineRowBorder and wrapElement carried in the flag-off path through the
    // passthrough context, which RowBase reads and applies on the <li>. The
    // provider renders no DOM, so the <li> stays a direct child of the <ol>.
    return <TimelineRowPassthroughContext value={eventRowPassthrough}>{itemToRender}</TimelineRowPassthroughContext>
  }

  // Flag-off (production) path: DOM and refs are byte-for-byte unchanged from before this
  // change. The highlight ref is attached to the event's Timeline.Item AND the
  // TimelineRowBorder container (see flagOffHighlightRef above); React's post-order ref
  // commit resolves `.current` to the TimelineRowBorder container, matching production.
  return wrapElement(wrappedElement, leadingElement, timelineItemKey(item))
}

const TimelineItemAvatar = ({
  item,
  addDivider,
}: {
  item: Extract<IssueTimelineItemFragment, {readonly __typename: 'IssueComment'}>
  addDivider: boolean
}) => {
  const {avatarUrl, login, profileUrl} = item.author ?? VALUES.ghost
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const hovercardAttrs = getHovercardAttributesForType('user', {login})

  return (
    <Link
      href={profileUrl ?? undefined}
      {...(profileUrl ? hovercardAttrs : {})}
      aria-label={`@${login}'s profile`}
      className={`${profileUrl ? styles.avatarLink : styles.avatarWithoutLink} ${styles.avatarOuter}`}
    >
      <GitHubAvatar
        key={login}
        alt={login}
        src={avatarUrl}
        size={40}
        className={`${styles.issueViewerAvatar} ${addDivider ? styles.avatarWithDivider : styles.avatarWithoutDivider}`}
      />
    </Link>
  )
}
